import React, { useState, useEffect, useRef } from "react";

import { productApi } from "../../services";
import { Alert, Button, Spinner } from "../../../components/ui";
import "./background-removal-buttons.scss";

/**
 * Admin-only overlay button shown on top-right of the product preview's
 * first image. Triggers server-side bg-removal with u2net and inserts the
 * result as a new image immediately after the original.
 *
 * (birefnet-general-lite was the second button but OOM-killed even on the
 * 3.8GB staging EC2; reverted to u2net-only until we upsize the host.)
 */
const BUTTONS = [
  {
    label: "Remove background (u2net)",
    model: "u2net",
    eta: "~5s",
  },
];

const BackgroundRemovalButtons = ({ productId, onSuccess }) => {
  const [pendingModel, setPendingModel] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const tickRef = useRef(null);

  // While pending, count seconds elapsed so the user can see progress.
  useEffect(() => {
    if (pendingModel) {
      setElapsedSec(0);
      tickRef.current = setInterval(() => {
        setElapsedSec((s) => s + 1);
      }, 1000);
    } else {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    }
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [pendingModel]);

  const handleClick = async (model) => {
    if (pendingModel) return;

    setPendingModel(model);
    setError(null);

    try {
      await productApi.removeBackground(productId, model);
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Background removal failed";
      setError(msg);
    } finally {
      setPendingModel(null);
    }
  };

  const dots = ".".repeat((elapsedSec % 3) + 1);

  return (
    <div className="bg-removal-buttons">
      {BUTTONS.map(({ label, model, eta }) => {
        const isPending = pendingModel === model;
        const disabled = pendingModel !== null;

        return (
          <Button
            key={model}
            variant="secondary"
            size="sm"
            onClick={() => handleClick(model)}
            disabled={disabled}
            aria-busy={isPending || undefined}
            leadingIcon={isPending ? <Spinner size="xs" inline label="Processing" /> : null}
            data-btn="image-background-remove"
          >
            {isPending ? `Processing${dots} ${elapsedSec}s (${eta})` : label}
          </Button>
        );
      })}

      {error && <Alert tone="error">{error}</Alert>}
    </div>
  );
};

export default BackgroundRemovalButtons;
